import { isLanguage, type Language } from '@/stores/languageStore';
import { tenantHref } from '@/routing/tenant';

/**
 * Query parameter carrying the language the QR was produced in.
 *
 * Spelled `lang` rather than i18next's `lng` because this URL is printed on a
 * poster and occasionally typed by hand; the extra vowel costs nothing and the
 * app does not use i18next's own querystring detector.
 */
export const REGISTRATION_LANG_PARAM = 'lang';

/**
 * The public self-registration URL for a clinic, in a chosen language.
 *
 * Built from the browser's own origin rather than a configured public base URL
 * — there isn't one — so the link is always correct for whichever host the
 * staff member is actually on (dev, LAN, production).
 *
 * Addressed by the clinic's slug rather than its id: this string is printed on a
 * poster, so `…/acme/p/register` is both shorter to encode and legible to
 * someone typing it off the wall. Links printed before slugs existed carry an id
 * instead and are forwarded by `LegacyPublicRedirect`.
 */
export function buildRegistrationUrl(
  origin: string,
  clinicSlug: string,
  language: Language,
): string {
  const url = new URL(tenantHref(clinicSlug, '/p/register'), origin);
  url.searchParams.set(REGISTRATION_LANG_PARAM, language);
  return url.toString();
}

/**
 * Basename for the downloaded PNG. The language is part of it so a clinic
 * printing all three posters ends up with three files rather than one
 * overwritten three times.
 */
export function registrationQrFileBase(clinicSlug: string, language: Language): string {
  return `register-${clinicSlug}-${language}`;
}

/**
 * The language a scanned link asks for, or null when it asks for nothing
 * recognisable.
 *
 * Null rather than a default, so the caller can leave the visitor's own
 * detected language alone instead of forcing English onto a link that predates
 * this parameter.
 */
export function readRegistrationLanguage(params: URLSearchParams): Language | null {
  const value = params.get(REGISTRATION_LANG_PARAM);
  return isLanguage(value) ? value : null;
}
